import { useSyncExternalStore } from "react";

const SET_GLOBALS_EVENT_TYPE = "openai:set_globals";

interface OpenAiGlobals {
  theme?: "light" | "dark";
  displayMode?: "inline" | "fullscreen" | "pip";
  userAgent?: {
    device?: {
      type?: "mobile" | "tablet" | "desktop" | "unknown";
    };
    capabilities?: {
      hover?: boolean;
      touch?: boolean;
    };
  };
  safeArea?: {
    insets?: {
      top: number;
      bottom: number;
      left: number;
      right: number;
    };
  };
  widgetState?: any;
  maxHeight?: number;
  locale?: string;
  toolOutput?: any;
}

declare global {
  interface Window {
    openai?: OpenAiGlobals & {
      requestDisplayMode?: (options: { mode: string }) => Promise<any>;
      sendFollowUpMessage?: (options: { prompt: string }) => void;
      setWidgetState?: (state: any) => Promise<void>;
    };
  }
}

/**
 * Hook that subscribes to a specific window.openai global value
 * and reactively updates when the host changes it via openai:set_globals event.
 */
export function useOpenAiGlobal<K extends keyof OpenAiGlobals>(key: K): OpenAiGlobals[K] | undefined {
  return useSyncExternalStore(
    (onChange) => {
      const handleSetGlobal = (event: CustomEvent) => {
        const value = event.detail?.globals?.[key];
        if (value === undefined) {
          return;
        }
        onChange();
      };

      window.addEventListener(SET_GLOBALS_EVENT_TYPE, handleSetGlobal as EventListener, {
        passive: true,
      });

      return () => {
        window.removeEventListener(SET_GLOBALS_EVENT_TYPE, handleSetGlobal as EventListener);
      };
    },
    () => window.openai?.[key]
  );
}

/**
 * Hook to get the current theme ("light" or "dark")
 */
export function useTheme(): "light" | "dark" {
  return useOpenAiGlobal("theme") || "light";
}

/**
 * Hook to get the device type with accuracy from the host
 */
export function useDeviceType(): "mobile" | "tablet" | "desktop" | "unknown" {
  return useOpenAiGlobal("userAgent")?.device?.type || "unknown";
}

/**
 * Hook to get user agent capabilities (hover, touch support)
 */
export function useDeviceCapabilities(): { hover: boolean; touch: boolean } {
  return useOpenAiGlobal("userAgent")?.capabilities || { hover: false, touch: false };
}

/**
 * Hook to get safe area insets (for notched devices)
 */
export function useSafeArea(): { top: number; bottom: number; left: number; right: number } {
  return useOpenAiGlobal("safeArea")?.insets || {
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  };
}

/**
 * Hook to get the current display mode
 */
export function useDisplayMode(): "inline" | "fullscreen" | "pip" {
  return useOpenAiGlobal("displayMode") || "inline";
}

/**
 * Hook to get the persisted widget state
 */
export function useWidgetState(): any | null {
  return useOpenAiGlobal("widgetState");
}

/**
 * Hook to get the maximum height constraint for the widget
 */
export function useMaxHeight(): number {
  return useOpenAiGlobal("maxHeight") || 0;
}

/**
 * Hook to get the current locale
 */
export function useLocale(): string {
  return useOpenAiGlobal("locale") || "en-US";
}

/**
 * Hook to determine if device supports hover (vs touch-only)
 */
export function useHoverSupport(): boolean {
  const capabilities = useDeviceCapabilities();
  return capabilities.hover ?? false;
}

/**
 * Hook to determine if device is touch-capable
 */
export function useTouchSupport(): boolean {
  const capabilities = useDeviceCapabilities();
  return capabilities.touch ?? false;
}

